import { MenuPopover } from '@reach/menu-button';
import { Form } from 'informed';
import PropTypes from 'prop-types';

import Combobox, { ComboboxItemsProptypes } from '../Forms/Combobox/Combobox';

import DropdownMenu from './DropdownMenu_DEPRECATED';
import MenuHeading from './MenuHeading_DEPRECATED';

import './styles/globalDropdownMenuStyles.css';
import './styles/menuStyles.css';

/**
 * some notes on pain points related to downshift/reach clash:
 * 1) keyboard navigation wasn't catching the MenuLinks (it is essentially navigating through the downshift li's),
 *    so we have to navigate onItemSelect (passed down from consumer). Note that MenuLinks should still be used as
 *    they provide necessary styling and cause the popover to close when they are selected.
 * 2) combobox holds the value that was selected and doesn't clear automatically between selections
 *    we tried resetting the value onItemClick or onBlur, but there is a race condition with downshift
 *    actually setting that item as the displayed value. This could be fixed with a setTimeout....but then
 *    we have a setTimeout. We got around this issue by setting the displayValue of the combobox item to an
 *    empty string.
 */

//  This component should be deprecated in favor of the <MenuButton/> in the __pds folder, however the custom styling, and integrations with downshift, informed, etc
//  requires it to have its own custom component. Even though React 18 is technically incompatible with reach UI's portals, we will be using this component in Phoebe because its placement does
//  does not result in buggy behavior. We will remove it when pds does a complete overhaul.

const MAX_VISIBLE = 8;
const MIN_COMBOBOX_ITEMS = 9;

const GlobalPrimaryDropdownMenu = ({
  children,
  comboboxProps,
  dropdownMenuProps,
  i18n,
  items,
  minComboboxItems,
  ...rest
}) => {
  const isComboboxVisible = items.length >= minComboboxItems;
  const cboxProps = { field: 'workspace-switcher', ...comboboxProps };

  return isComboboxVisible ? (
    <MenuPopover
      className="bg-white border border-gray-2 rounded-sm shadow-dropdown overflow-hidden max-w-sm w-4/12 global-primary-dropdown"
      {...rest}
    >
      <Form>
        <Combobox
          {...cboxProps}
          dropdownHeaderText={i18n.comboboxDropdownHeader}
          hideDownButton
          hideLabel
          items={items}
          itemsAlwaysOpen
          labelText={i18n.labelText}
          maxVisible={MAX_VISIBLE}
          noBorder
          noResultsText={i18n.noResultsText}
          placeholder={i18n.placeholder}
        />
      </Form>
      {children}
    </MenuPopover>
  ) : (
    <DropdownMenu {...dropdownMenuProps}>
      {i18n.menuDropdownHeader && (
        <MenuHeading>{i18n.menuDropdownHeader}</MenuHeading>
      )}
      {items.map((item) => {
        return item.isUnprivileged ? (
          <div className="unprivileged-item" key={item.value}>
            {item.content}
          </div>
        ) : (
          <div key={item.value}>{item.content}</div>
        );
      })}
      {children}
    </DropdownMenu>
  );
};

GlobalPrimaryDropdownMenu.defaultProps = {
  children: null,
  comboboxProps: {},
  dropdownMenuProps: {},
  minComboboxItems: MIN_COMBOBOX_ITEMS,
};

GlobalPrimaryDropdownMenu.propTypes = {
  children: PropTypes.node,
  comboboxProps: PropTypes.shape({}),
  dropdownMenuProps: PropTypes.shape({}),
  /**
   * Internationalization object to display content in proper language
   */
  i18n: PropTypes.shape({
    comboboxDropdownHeader: PropTypes.string,
    labelText: PropTypes.string.isRequired,
    menuDropdownHeader: PropTypes.string,
    noResultsText: PropTypes.string,
    placeholder: PropTypes.string,
  }).isRequired,
  /**
   * Items to display in the combobox list
   */
  items: ComboboxItemsProptypes.isRequired,
  /**
   * Minimum number of items to display
   */
  minComboboxItems: PropTypes.number,
};

export default GlobalPrimaryDropdownMenu;
